// LiveWorkouts.tsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './LiveWorkouts.module.scss';
import SignalWifiOffRoundedIcon from '@mui/icons-material/SignalWifiOffRounded';
import { locales } from '../../constants';
import ContentDrill from '../composites/ContentDrill';
import LiveAthleteView from './LiveAthleteView';
import LiveGroupView from './LiveGroupView';
import TabSlider, { TabSliderTab } from '../composites/TabSlider';
import LiveRackView from './LiveRackView';
import { LiveRepFrame } from '../../types/automatic';
import { useGateway } from '../../providers';
import { useSessionAction, useSessionData, useUserAction } from '../../providers';
import type { TrainingRoutineSession } from '../../types';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { AutomaticMetricsType } from '../../constants';
import { JetsonHealthStatus } from '../../constants/health';
import type { JetsonHealthDTO } from '../../types/jetson';

const liveWorkoutMenuProps = {
  PaperProps: {
    sx: {
      minWidth: 180,
      maxWidth: 'min(340px, calc(100vw - 32px))',
      overflowX: 'auto',
    },
  },
  MenuListProps: {
    sx: {
      width: 'max-content',
      minWidth: '100%',
      overflowX: 'auto',
      '& .MuiMenuItem-root': {
        whiteSpace: 'nowrap',
      },
    },
  },
};

const texts = locales.en.components.liveWorkouts.liveWorkouts;

enum ContentTabs {
  RackView = 'Rack View',
  AthleteView = 'Athlete View',
  GroupView = 'Group Stats',
}

const LiveWorkouts: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const {
    status,
    presenceByStation,
    liveSessionsByStation,
    connect,
    disconnect,
  } = useGateway();
  const { gym, members, groups, loginTrainer, loginManager, loginMember } =
    useSessionData();
  const { ensureExerciseLibraryLoaded, hydrateLiveWorkoutsDataForGym } =
    useSessionAction();
  const { getLastSessions, getJetsonsHealth } = useUserAction();

  useEffect(() => {
    if (!gym?.id) {
      return;
    }
    void ensureExerciseLibraryLoaded();
    void hydrateLiveWorkoutsDataForGym(gym.id);
  }, [ensureExerciseLibraryLoaded, gym?.id, hydrateLiveWorkoutsDataForGym]);

  // --- LiveAthleteView set aggregation logic (reused shape) ---
  interface SetInfo {
    userId: string;
    metrics: LiveRepFrame[];
    rackNumber: number;
    performedSets: number;
    setId: string;
    concentricType: AutomaticMetricsType;
    eccentricType: AutomaticMetricsType;
    finishedAt?: number;
    source?: 'live' | 'historical' | 'liveFinished';
    performedReps?: number;
  }

  function generateSetId(userId: string, rackNumber: number, seed?: string) {
    // Use a high-entropy seed to avoid collisions across multiple sets per member
    const s =
      seed || Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    return `${userId}-${rackNumber}-${s}`;
  }

  const parseRackNumber = (rackNum?: string | null): number | undefined => {
    const parsed = Number(rackNum);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };

  const getNextAvailableNumber = (taken: Set<number>): number => {
    let candidate = 1;
    while (taken.has(candidate)) {
      candidate += 1;
    }
    return candidate;
  };

  function extractCurrentSets(
    data: Record<string, LiveRepFrame[]>,
    prevLiveSets: SetInfo[],
  ): SetInfo[] {
    const sets: SetInfo[] = [];
    // Build a quick map to keep stable IDs across renders per (userId|rack)
    const prevByUserRack: Record<string, SetInfo> = {};
    for (const s of prevLiveSets) {
      prevByUserRack[`${s.userId}|${s.rackNumber}`] = s;
    }
    for (const userId of Object.keys(data)) {
      const metrics = data[userId] || [];
      if (metrics.length === 0) continue;
      const lastFrame = metrics[metrics.length - 1];
      const rackNumber = lastFrame.rackNumber ?? 1;
      const performedSets = lastFrame.preset?.performedSets ?? 1;
      const existing = prevByUserRack[`${userId}|${rackNumber}`];
      const setId = existing
        ? existing.setId
        : generateSetId(userId, rackNumber);
      sets.push({
        userId,
        metrics,
        rackNumber,
        performedSets,
        setId,
        concentricType: AutomaticMetricsType.Tempo,
        eccentricType: AutomaticMetricsType.Tempo,
        source: 'live',
      });
    }
    return sets;
  }

  const [liveSets, setLiveSets] = useState<SetInfo[]>([]);
  const [finishedSets, setFinishedSets] = useState<SetInfo[]>([]);
  const [jetsonsHealth, setJetsonsHealth] = useState<JetsonHealthDTO[]>([]);
  const prevDataRef = React.useRef<Record<string, LiveRepFrame[]>>({});

  // --- Handler for clicking a rack ---
  const handleRackClick = useCallback((rackNumber: number) => {
    setSelectedRackNumber(String(rackNumber));
    setActiveTab(1); // AthleteView
  }, []);

  // --- Station mapping and aggregation state for RackView ---
  const stationIds = useMemo(
    () => Object.keys(presenceByStation).sort(),
    [presenceByStation],
  );
  const stationIndexMap = useMemo(() => {
    const map: Record<string, number> = {};
    stationIds.forEach((sid, i) => {
      const num = presenceByStation[sid]?.stationNumber;
      map[sid] =
        Number.isFinite(num as number) && (num as number) > 0
          ? (num as number)
          : i + 1;
    });
    return map;
  }, [stationIds, presenceByStation]);

  const [racksAggregatedData, setRacksAggregatedData] = useState<
    Record<
      string,
      {
        stationNumber?: number;
        currentMember?: string | null;
        status?: string;
        rtServiceOnline?: boolean;
        data: Record<string, LiveRepFrame[]>;
        order: string[];
        activeUserId: string | null;
      }
    >
  >({});

  // Keep racks aggregated based on liveSessionsByStation
  useEffect(() => {
    setRacksAggregatedData((prev) => {
      let next = { ...prev };
      // Reset racks that no longer exist (by stationId now)
      const validIds = new Set<string>(stationIds);
      Object.keys(next).forEach((sid) => {
        if (!validIds.has(sid)) delete (next as any)[sid];
      });

      // 1) Seed from presence so activeMembers are always visible
      for (const sid of stationIds) {
        const presence = presenceByStation[sid];
        const activeMembers = presence?.activeMembers || [];
        const stationNumber = presence?.stationNumber;
        const currentMember = presence?.currentMember ?? null;
        const status = presence?.status;
        const rtServiceOnline = presence?.rtServiceOnline;
        const demoSnapshots = (
          presence?.details as {
            demoSnapshots?: Record<string, LiveRepFrame[]>;
          }
        )?.demoSnapshots;
        // Preserve previous metrics so colors persist after session_ended
        const existing = next[sid] || {
          stationNumber,
          currentMember,
          status,
          rtServiceOnline,
          data: {},
          order: [],
          activeUserId: null,
        };
        const prevData: Record<string, LiveRepFrame[]> = existing.data || {};
        const dataMap: Record<string, LiveRepFrame[]> = {};
        // Keep only active members, preserving their latest metrics if present
        for (const mId of activeMembers) {
          if (demoSnapshots?.[mId]?.length) {
            dataMap[mId] = demoSnapshots[mId].map((frame) => ({ ...frame }));
          } else {
            dataMap[mId] = prevData[mId] ?? [];
          }
        }
        // Build a stable order: keep existing order filtered to active members, then append new ones
        const existingOrder = (existing.order || []).filter((id) =>
          activeMembers.includes(id),
        );
        const newOnes = activeMembers.filter(
          (id) => !existingOrder.includes(id),
        );
        const order: string[] = [...existingOrder, ...newOnes];
        next[sid] = {
          stationNumber,
          currentMember,
          status,
          rtServiceOnline,
          data: dataMap,
          order,
          // no highlight in presence-only state
          activeUserId: null,
        };
      }

      // 2) Overlay any live session metrics on top of presence baseline
      for (const [stationId, sess] of Object.entries(liveSessionsByStation)) {
        if (!sess) continue;
        const stationNumber =
          sess.meta?.stationNumber ??
          presenceByStation[stationId]?.stationNumber ??
          stationIndexMap[stationId];
        const metricsArray = Array.isArray(sess.repFrames)
          ? sess.repFrames
          : [];
        const resolvedStationNumber =
          typeof stationNumber === 'number' ? stationNumber : undefined;
        const normalizedMetrics =
          metricsArray.length && resolvedStationNumber !== undefined
            ? metricsArray.some(
              (frame) => frame?.rackNumber !== resolvedStationNumber,
            )
              ? metricsArray.map((frame) => ({
                ...frame,
                rackNumber: resolvedStationNumber,
              }))
              : metricsArray
            : metricsArray;
        const existing = next[stationId] || {
          stationNumber,
          currentMember: presenceByStation[stationId]?.currentMember ?? null,
          status: presenceByStation[stationId]?.status,
          rtServiceOnline: presenceByStation[stationId]?.rtServiceOnline,
          data: {},
          order: [],
          activeUserId: null,
        };
        const dataMap: Record<string, LiveRepFrame[]> = { ...existing.data };
        dataMap[sess.meta.memberId] = normalizedMetrics;
        const order = existing.order.includes(sess.meta.memberId)
          ? existing.order
          : [...existing.order, sess.meta.memberId];
        const shouldHighlight = !sess.completed;
        next[stationId] = {
          ...existing,
          stationNumber,
          data: dataMap,
          order,
          // during live session, highlight the performing member
          activeUserId: shouldHighlight
            ? sess.meta.memberId
            : (existing.activeUserId ?? null),
        };
      }

      return next;
    });
  }, [stationIds, presenceByStation, liveSessionsByStation, stationIndexMap]);

  // --- LiveAthleteView source: merge all racksAggregatedData into single athleteViewData ---
  const athleteViewData = useMemo(() => {
    // Only expose metrics for the currently performing member per station.
    // This ensures that when session_ended clears activeUserId, the live set disappears
    // from the source, and our effect moves it to finished immediately.
    const merged: Record<string, LiveRepFrame[]> = {};
    Object.values(racksAggregatedData).forEach((rack) => {
      const performingUserId = rack.activeUserId;
      if (performingUserId) {
        const arr = rack.data[performingUserId] || [];
        if (arr.length) {
          merged[performingUserId] = arr;
        }
      }
    });
    return merged;
  }, [racksAggregatedData]);

  // --- Update liveSets and finishedSets on athleteViewData change ---
  useEffect(() => {
    const currentSets = extractCurrentSets(athleteViewData, liveSets);
    // Map of current sets by rackNumber
    const rackMap: Record<number, SetInfo> = {};
    for (const s of currentSets) {
      rackMap[s.rackNumber] = s;
    }
    // Compute new liveSets and finishedSets synchronously
    let newLiveSets: SetInfo[] = [...liveSets];
    let finishedToAdd: SetInfo[] = [];
    // Remove or replace old sets
    for (let i = newLiveSets.length - 1; i >= 0; i--) {
      const oldSet = newLiveSets[i];
      const replacement = rackMap[oldSet.rackNumber];
      if (!replacement || replacement.userId !== oldSet.userId) {
        finishedToAdd.push({
          ...oldSet,
          finishedAt: Date.now(),
          source: 'liveFinished',
        });
        newLiveSets.splice(i, 1);
      }
    }
    // Add or update new sets
    for (const newSet of currentSets) {
      const existingIndex = newLiveSets.findIndex(
        (ls) =>
          ls.userId === newSet.userId &&
          ls.rackNumber === newSet.rackNumber &&
          ls.setId === newSet.setId,
      );
      if (existingIndex > -1) {
        newLiveSets[existingIndex] = { ...newSet };
      } else {
        // If user had previous set on same rack (regardless of performedSets), move that one to finished
        const oldUserIndex = newLiveSets.findIndex(
          (ls) =>
            ls.userId === newSet.userId &&
            ls.rackNumber === newSet.rackNumber &&
            ls.setId !== newSet.setId,
        );
        if (oldUserIndex > -1) {
          const oldSet = newLiveSets[oldUserIndex];
          finishedToAdd.push({
            ...oldSet,
            finishedAt: Date.now(),
            source: 'liveFinished',
          });
          newLiveSets.splice(oldUserIndex, 1);
        }
        newLiveSets.unshift(newSet);
      }
    }
    // Update both states at once
    setLiveSets(newLiveSets);
    if (finishedToAdd.length > 0) {
      setFinishedSets((prevFinished) => {
        const updated = [...finishedToAdd, ...prevFinished].filter(
          (set) => !newLiveSets.some((live) => live.setId === set.setId),
        );
        // sort by finishedAt desc, keep unique by setId
        const seen = new Set<string>();
        const uniqueSorted = updated
          .filter((s) => {
            if (seen.has(s.setId)) return false;
            seen.add(s.setId);
            return true;
          })
          .sort((a, b) => (b.finishedAt || 0) - (a.finishedAt || 0));
        return uniqueSorted;
      });
    }
    prevDataRef.current = athleteViewData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteViewData]);

  // --- Initial connect and teardown tied to active flag ---
  const shouldSkipStrictCleanupRef = React.useRef(
    process.env.NODE_ENV !== 'production',
  );
  useEffect(() => {
    if (gym?.id) {
      const userId =
        loginTrainer?.id || loginManager?.id || loginMember?.id || undefined;
      const identity = {
        gymId: gym.id,
        userId,
        authToken: undefined, // TODO: wire real auth token when available.
      };
      connect(['*'], identity);
    } else {
      disconnect();
    }
    return () => {
      if (shouldSkipStrictCleanupRef.current) {
        shouldSkipStrictCleanupRef.current = false;
        return;
      }
      disconnect();
    };
  }, [
    connect,
    disconnect,
    gym?.id,
    loginTrainer?.id,
    loginManager?.id,
    loginMember?.id,
  ]);

  useEffect(() => {
    // Preload finished sets from last sessions when members ready
    const load = async () => {
      const memberIds = Object.keys(members || {});
      if (!memberIds.length) return;
      const last = await getLastSessions(memberIds);
      if (!last) return;
      // Convert last TrainingRoutineSession into finished SetInfo where analyzedMetrics exist and status Complete
      const finishedFromSessions: SetInfo[] = [];
      Object.values(last).forEach((session: TrainingRoutineSession) => {
        const pr = (session as any)?.performedRoutine;
        const pegArr = pr?.performedExerciseGroups as any[] | undefined;
        if (!Array.isArray(pegArr)) return;
        pegArr.forEach((peg) => {
          const setGroups = (peg as any)?.sets as any[] | undefined;
          if (!Array.isArray(setGroups)) return;
          setGroups.forEach((psg) => {
            const sets = Array.isArray(psg) ? psg : [];
            sets.forEach((ps) => {
              const status = (ps as any)?.status;
              if (status !== 'Complete') return;
              const analyzedRaw = (ps as any)?.analyzedMetrics as any;
              const latest =
                Array.isArray(analyzedRaw) && analyzedRaw.length > 0
                  ? analyzedRaw.filter(Boolean)[analyzedRaw.length - 1]
                  : typeof analyzedRaw === 'object' && analyzedRaw !== null
                    ? analyzedRaw
                    : null;
              if (!latest || typeof latest !== 'object') return;
              const psStationId = (ps as any)?.stationId as string | undefined;
              const psStationNumberRaw = (ps as any)?.stationNumber as
                | number
                | undefined;
              if (!psStationId) return;
              const keys = Object.keys(latest).sort(
                (a, b) => Number(a) - Number(b),
              );
              const repsCount = keys.length ? Number(keys[keys.length - 1]) : 0;
              if (!Number.isFinite(repsCount) || repsCount <= 0) return;
              const metricsArray: LiveRepFrame[] = [];
              const cType =
                (ps as any)?.metrics?.concentric?.type ??
                AutomaticMetricsType.Tempo;
              const eType =
                (ps as any)?.metrics?.eccentric?.type ??
                AutomaticMetricsType.Tempo;
              const cMin = (ps as any)?.metrics?.concentric?.min;
              const cMax = (ps as any)?.metrics?.concentric?.max;
              const eMin = (ps as any)?.metrics?.eccentric?.min;
              const eMax = (ps as any)?.metrics?.eccentric?.max;
              const stationNumberForRack =
                (psStationNumberRaw as number | undefined) ??
                presenceByStation[psStationId]?.stationNumber ??
                stationIndexMap[psStationId] ??
                1;
              for (let i = 1; i <= repsCount; i++) {
                const rep = (latest as any)[String(i)];
                if (!rep) continue;
                metricsArray.push({
                  rackNumber: stationNumberForRack,
                  up: true,
                  down: true,
                  rep: i,
                  upDuration:
                    cType === AutomaticMetricsType.Velocity
                      ? rep.upVelocity
                      : rep.upDuration,
                  downDuration:
                    eType === AutomaticMetricsType.Velocity
                      ? rep.downVelocity
                      : rep.downDuration,
                  memberId: session.memberId,
                  exerciseId: (ps as any).exerciseId,
                  preset: {
                    performedSets: 1,
                    isAmrap: (ps as any)?.isAmrap,
                    reps:
                      typeof (ps as any)?.reps === 'number'
                        ? (ps as any).reps
                        : repsCount,
                    weight:
                      (ps as any).performedWeight ??
                      (ps as any).weight ??
                      undefined,
                    minConcentric: cMin,
                    maxConcentric: cMax,
                    minEccentric: eMin,
                    maxEccentric: eMax,
                    concentricType: cType,
                    eccentricType: eType,
                  },
                });
              }
              if (metricsArray.length === 0) return;
              const rack =
                (psStationNumberRaw as number | undefined) ??
                presenceByStation[psStationId]?.stationNumber ??
                stationIndexMap[psStationId] ??
                1;
              const finishedAt =
                (ps as any).finishedTimestamp ||
                new Date(session.createdDate).getTime() ||
                Date.now();
              const setId = `${session.id}-${(ps as any).exerciseId}-${finishedAt}`;
              finishedFromSessions.push({
                userId: session.memberId,
                metrics: metricsArray,
                rackNumber: rack,
                performedSets: 1,
                setId,
                concentricType: cType,
                eccentricType: eType,
                finishedAt,
                source: 'historical',
                performedReps: (ps as any)?.performedReps ?? undefined,
              });
            });
          });
        });
      });
      if (finishedFromSessions.length) {
        // sort by finishedAt desc
        finishedFromSessions.sort(
          (a, b) => (b.finishedAt || 0) - (a.finishedAt || 0),
        );
        setFinishedSets((prev) => {
          const merged = [...finishedFromSessions, ...prev];
          // de-duplicate by setId
          const seen = new Set<string>();
          const unique = merged.filter((s) => {
            if (seen.has(s.setId)) return false;
            seen.add(s.setId);
            return true;
          });
          return unique;
        });
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, getLastSessions, presenceByStation, stationIndexMap]);

  useEffect(() => {
    let isMounted = true;
    const loadHealth = async () => {
      if (!gym?.id) {
        if (isMounted) setJetsonsHealth([]);
        return;
      }
      const data = await getJetsonsHealth(gym.id);
      if (isMounted) {
        setJetsonsHealth(data || []);
      }
    };
    loadHealth();
    return () => {
      isMounted = false;
    };
  }, [gym?.id, getJetsonsHealth]);

  // --- Persist filters for LiveAthleteView ---
  const [selectedGroupId, setSelectedGroupId] = useState<string>('all');
  const [selectedAthleteId, setSelectedAthleteId] = useState<string>('all');
  const [selectedRackNumber, setSelectedRackNumber] = useState<string>('all');

  // --- Apply filters to stationIds for RackView ---
  const filteredStationIds = useMemo(() => {
    const groupMemberSet =
      selectedGroupId !== 'all'
        ? new Set(groups[selectedGroupId]?.memberIds || [])
        : null;
    return stationIds.filter((sid) => {
      // Check presence activeMembers for quick filter
      const presence = presenceByStation[sid];
      const active = new Set(presence?.activeMembers || []);
      const hasAthlete =
        selectedAthleteId === 'all' || active.has(selectedAthleteId);
      const hasGroup =
        !groupMemberSet ||
        [...active].some((m) => groupMemberSet && groupMemberSet.has(m));
      // Also consider aggregated rack data (liveSessions) if presence doesn't have it
      const rackData = racksAggregatedData[sid];
      const data = rackData?.data || {};
      const userIds = Object.keys(data);
      const liveHasAthlete =
        selectedAthleteId === 'all' || userIds.includes(selectedAthleteId);
      const liveHasGroup =
        !groupMemberSet ||
        userIds.some((m) => groupMemberSet && groupMemberSet.has(m));
      return (hasAthlete && hasGroup) || (liveHasAthlete && liveHasGroup);
    });
  }, [
    stationIds,
    presenceByStation,
    selectedAthleteId,
    selectedGroupId,
    groups,
    racksAggregatedData,
  ]);

  const healthFallbackRacks = useMemo(() => {
    const existingStationIds = new Set(Object.keys(racksAggregatedData));
    const takenNumbers = new Set<number>();
    Object.entries(racksAggregatedData).forEach(([sid, agg]) => {
      const num =
        (agg?.stationNumber as number | undefined) ??
        (stationIndexMap[sid] as number | undefined);
      if (Number.isFinite(num)) {
        takenNumbers.add(num as number);
      }
    });

    const seenHealthIds = new Set<string>();
    const fallback: Array<{
      stationId: string;
      stationNumber: number;
      status?: JetsonHealthStatus;
    }> = [];

    jetsonsHealth.forEach((health) => {
      const stationId =
        health.socId ||
        (health.rackNum ? `health-rack-${health.rackNum}` : undefined);
      if (!stationId || existingStationIds.has(stationId)) return;
      if (seenHealthIds.has(stationId)) return;
      seenHealthIds.add(stationId);

      let stationNumber =
        parseRackNumber(health.rackNum) ?? getNextAvailableNumber(takenNumbers);
      if (takenNumbers.has(stationNumber)) {
        stationNumber = getNextAvailableNumber(takenNumbers);
      }
      takenNumbers.add(stationNumber);

      fallback.push({
        stationId,
        stationNumber,
        status: health.status,
      });
    });

    return fallback;
  }, [jetsonsHealth, racksAggregatedData, stationIndexMap]);

  const mergedRacksAggregatedData = useMemo(() => {
    const additions: typeof racksAggregatedData = {};
    healthFallbackRacks.forEach((rack) => {
      additions[rack.stationId] = {
        stationNumber: rack.stationNumber,
        currentMember: null,
        status:
          rack.status === JetsonHealthStatus.CAMERA_CONNECTION_ERROR
            ? 'CAMERA_ISSUE'
            : 'DOWN',
        rtServiceOnline: false,
        data: {},
        order: [],
        activeUserId: null,
      };
    });
    return {
      ...racksAggregatedData,
      ...additions,
    };
  }, [healthFallbackRacks, racksAggregatedData]);

  const combinedStationIds = useMemo(() => {
    const ids = [...filteredStationIds];
    healthFallbackRacks.forEach((rack) => {
      if (!ids.includes(rack.stationId)) {
        ids.push(rack.stationId);
      }
    });
    return ids;
  }, [filteredStationIds, healthFallbackRacks]);

  // --- TabSlider integration ---
  const tabSliderTabs: TabSliderTab[] = [
    {
      label: ContentTabs.RackView,
      content: (
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <FormControl size="small" style={{ minWidth: 180 }}>
              <InputLabel className={styles.inputLabel} id="rack-group-filter">
                Group
              </InputLabel>
              <Select
                labelId="rack-group-filter"
                value={selectedGroupId}
                label="Group"
                onChange={(e) => setSelectedGroupId(e.target.value as string)}
                className={styles.select}
                autoWidth
                MenuProps={liveWorkoutMenuProps}
              >
                <MenuItem value="all">All Groups</MenuItem>
                {Object.values(groups).map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" style={{ minWidth: 180 }}>
              <InputLabel
                className={styles.inputLabel}
                id="rack-athlete-filter"
              >
                Athlete
              </InputLabel>
              <Select
                labelId="rack-athlete-filter"
                value={selectedAthleteId}
                label="Athlete"
                onChange={(e) => setSelectedAthleteId(e.target.value as string)}
                className={styles.select}
                autoWidth
                MenuProps={liveWorkoutMenuProps}
              >
                <MenuItem value="all">All Athletes</MenuItem>
                {Object.values(groups)
                  .flatMap((g) => g.memberIds)
                  .concat(Object.keys(members))
                  .filter((v, i, a) => a.indexOf(v) === i)
                  .map((mId) => ({
                    id: mId,
                    first:
                      members[mId]?.firstName ??
                      members[mId]?.name?.split(' ')?.[0] ??
                      '',
                    last:
                      members[mId]?.lastName ??
                      (members[mId]?.name &&
                        (members[mId]?.name as string).split(' ').length > 1
                        ? (members[mId]?.name as string).split(' ')[1]
                        : ''),
                  }))
                  .sort((a, b) => a.first.localeCompare(b.first))
                  .map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {`${m.first} ${m.last}`.trim()}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>
          <LiveRackView
            stationIds={combinedStationIds}
            racksAggregatedData={mergedRacksAggregatedData}
            stationIndexMap={stationIndexMap}
            onRackClick={handleRackClick}
          />
        </div>
      ),
    },
    {
      label: ContentTabs.AthleteView,
      content: (
        <LiveAthleteView
          liveSets={liveSets}
          finishedSets={finishedSets}
          selectedGroupId={selectedGroupId}
          setSelectedGroupId={setSelectedGroupId}
          selectedAthleteId={selectedAthleteId}
          setSelectedAthleteId={setSelectedAthleteId}
          selectedRackNumber={selectedRackNumber}
          setSelectedRackNumber={setSelectedRackNumber}
        />
      ),
    },
    {
      label: ContentTabs.GroupView,
      content: (
        <LiveGroupView
          liveSets={liveSets.map((s) => ({
            userId: s.userId,
            metrics: s.metrics,
            concentricType: s.concentricType,
            eccentricType: s.eccentricType,
          }))}
          finishedSets={finishedSets.map((s) => ({
            userId: s.userId,
            metrics: s.metrics,
            concentricType: s.concentricType,
            eccentricType: s.eccentricType,
          }))}
          selectedGroupId={selectedGroupId}
          setSelectedGroupId={setSelectedGroupId}
          selectedAthleteId={selectedAthleteId}
          setSelectedAthleteId={setSelectedAthleteId}
        />
      ),
    },
  ];

  // // Full screen overlay
  // const renderFullScreenOverlay = () => (
  //   <div className={styles.fullScreenOverlay}>
  //     <div className={styles.fullScreenTitle}>Live Workouts</div>
  //     <TabSlider
  //       tabs={tabSliderTabs}
  //       initialTab={0}
  //       rightActions={rightActions}
  //     />
  //   </div>
  // );

  const showLiveUnavailableOverlay =
    status !== 'connected' && combinedStationIds.length === 0;

  return (
    <ContentDrill title={texts.title} disableSearch isParent>
      <div className={styles.liveWorkoutsContentWrapper}>
        {showLiveUnavailableOverlay && (
          <div className={styles.offlineBanner}>
            No active environment detected. Live data unavailable.
          </div>
        )}
        <div className={styles.contentSurface}>
          <div
            className={styles.tabShell}
            style={{
              pointerEvents: showLiveUnavailableOverlay ? 'none' : 'auto',
              opacity: showLiveUnavailableOverlay ? 0.5 : 1,
            }}
          >
            <TabSlider
              tabs={tabSliderTabs}
              initialTab={0}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          </div>
          {showLiveUnavailableOverlay && (
            <div
              className={styles.offlineOverlay}
              role="alert"
              aria-live="assertive"
            >
              <div className={styles.offlinePanel}>
                <div className={styles.offlineIconCircle}>
                  <SignalWifiOffRoundedIcon className={styles.offlineIcon} />
                </div>
                <span className={styles.offlineTag}>Gateway disconnected</span>
                <h3>Live data unavailable</h3>
                <p>
                  We can&apos;t reach your gym environment, so sets are paused.
                </p>
                <ul className={styles.offlineChecklist}>
                  <li>
                    Confirm this browser has a stable internet connection.
                  </li>
                  <li>Verify the Motivision RT gateway is reachable.</li>
                  <li>
                    Ensure trainee tablets are online and running sessions.
                  </li>
                </ul>
                <p className={styles.offlineHint}>
                  Live metrics resume automatically once connected.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </ContentDrill>
  );
};

export default LiveWorkouts;
